import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { CircleHelp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { getInstruction, type InstructionKey } from './registry';

interface Props {
  contextKey: InstructionKey;
  /** Texto do botão. Se vazio, mostra apenas o ícone. */
  label?: string;
  /** Variante do botão. */
  variant?: 'ghost' | 'outline' | 'inline';
  className?: string;
  /** Tamanho do ícone. */
  iconSize?: number;
}

/**
 * Botão "❓ Como fazer" que abre um modal com Markdown estático.
 * Conteúdo carregado de `src/content/instructions/<contextKey>.md` em build-time.
 */
export function InstructionTrigger({
  contextKey,
  label = 'Como fazer',
  variant = 'ghost',
  className,
  iconSize = 14,
}: Props) {
  const [open, setOpen] = useState(false);
  const md = getInstruction(contextKey);

  if (!md) return null;

  // Title vem do primeiro `# h1` ou fallback para a chave
  const titleMatch = md.match(/^#\s+(.+)$/m);
  const title = titleMatch?.[1] ?? contextKey;

  if (variant === 'inline') {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <button
            type="button"
            className={cn(
              'inline-flex items-center gap-1 text-xs text-primary hover:underline',
              className,
            )}
          >
            <CircleHelp size={iconSize} />
            {label}
          </button>
        </DialogTrigger>
        <DrawerContent title={title} markdown={md} />
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={variant} size="sm" className={cn('text-xs', className)}>
          <CircleHelp size={iconSize} className="mr-1.5" />
          {label}
        </Button>
      </DialogTrigger>
      <DrawerContent title={title} markdown={md} />
    </Dialog>
  );
}

function DrawerContent({ title, markdown }: { title: string; markdown: string }) {
  // Remove H1 do conteúdo já que ele vira o DialogTitle
  const body = markdown.replace(/^#\s+.+$/m, '').trim();

  return (
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 font-brand">
          <CircleHelp className="h-5 w-5 text-primary" />
          {title}
        </DialogTitle>
      </DialogHeader>
      <div className="prose prose-sm max-h-[70vh] max-w-none overflow-y-auto">
        <ReactMarkdown
          components={{
            h1: ({ children }) => (
              <h1 className="font-brand text-lg font-bold text-foreground">{children}</h1>
            ),
            h2: ({ children }) => (
              <h2 className="mt-5 font-brand text-base font-semibold text-foreground">
                {children}
              </h2>
            ),
            h3: ({ children }) => (
              <h3 className="mt-4 font-brand text-sm font-semibold text-foreground">{children}</h3>
            ),
            p: ({ children }) => (
              <p className="my-2 text-sm leading-relaxed text-foreground">{children}</p>
            ),
            ul: ({ children }) => (
              <ul className="my-2 list-disc space-y-1 pl-6 text-sm text-foreground">{children}</ul>
            ),
            ol: ({ children }) => (
              <ol className="my-2 list-decimal space-y-1 pl-6 text-sm text-foreground">
                {children}
              </ol>
            ),
            li: ({ children }) => <li className="leading-relaxed">{children}</li>,
            strong: ({ children }) => (
              <strong className="font-semibold text-foreground">{children}</strong>
            ),
            code: ({ children }) => (
              <code className="rounded bg-secondary px-1 py-0.5 font-mono text-xs">{children}</code>
            ),
            blockquote: ({ children }) => (
              <blockquote className="my-2 border-l-2 border-primary/40 bg-primary/5 px-3 py-1 text-sm italic text-muted-foreground">
                {children}
              </blockquote>
            ),
            a: ({ href, children }) => (
              <a href={href} target="_blank" rel="noreferrer" className="text-primary underline">
                {children}
              </a>
            ),
          }}
        >
          {body}
        </ReactMarkdown>
      </div>
    </DialogContent>
  );
}
